import { Box, Card, CardActionArea, Typography } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { memo } from "react";

import { type InternalTool } from "./tools-registry";

const styles = {
  card: { borderColor: "divider" },
  action: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: 1.5,
    p: 2,
    height: "100%",
  },
  icon: {
    width: 36,
    height: 36,
    flex: "none",
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    bgcolor: "primary.50",
    color: "primary.main",
  },
} satisfies Record<string, SxProps<Theme>>;

/** Props for {@link ToolCard}. */
export type ToolCardProps = {
  /** The tool to render. */
  readonly tool: InternalTool;
  /** Called with the tool's path when the card is activated. */
  readonly onOpen: (path: string) => void;
};

/**
 * A single tool tile on the internal tools launcher: icon, name, and
 * description. Purely presentational; navigation is delegated to `onOpen`.
 * Memoized because it renders inside mapped lists.
 */
const ToolCardComponent = ({ tool, onOpen }: ToolCardProps) => {
  const ToolIcon = tool.icon;
  return (
    <Card variant="outlined" sx={styles.card}>
      <CardActionArea
        sx={styles.action}
        onClick={() => {
          onOpen(tool.path);
        }}
      >
        <Box sx={styles.icon}>
          <ToolIcon fontSize="small" />
        </Box>
        <Box>
          <Typography component="div" variant="subtitle2">
            {tool.name}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {tool.description}
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
};

export const ToolCard = memo(ToolCardComponent);
ToolCard.displayName = "ToolCard";
